/**
 * header-hide.js — the header below 768 px: it slides away when the page scrolls DOWN more than 100 px past the header's
 * bottom, and comes back on any scroll UP. Section 15 (theme 89.0, 22.9.2026), copied from the source's own code
 * (Inova, customElements.get('sticky-header') · onScrollHandler - review-hd-89/src-sticky-89.txt):
 *
 *   hidden  when scrollTop > headerBounds.bottom + firstScrollTop + 100  AND  scrollTop > the previous scrollTop
 *   shown   on any scroll up, and whenever the page is back above that line
 *
 * This file only writes `data-hd-hidden` on header-component; assets/base.css ("ההדר — נעלם בגלילה בטלפון") does the
 * moving, and only below 768, as the source's mobile-dock.css does (max-width: 767px). Above 768 the attribute has no
 * effect - the source's header stays there ('always'), and so does ours.
 * The scroll source is Tinker's own (@theme/scroll-container): the document on a phone, .page-wrapper from 990.
 *
 * Theme 106.0 (1.10.2026, head 60.0 yes) - the footer pushes the header out, at every width. In the source the header section is
 * sticky inside a parent that ends at the footer group's top, so when the footer arrives the header's bottom rides on the footer's
 * top and leaves with it (review-105/hdr106/end-ref.txt: 768, y 9870 - header -149..-52, footer top -52). Ours is sticky in the
 * whole page and stayed over the footer (17/23 links reachable at 768). Reproduced without touching layout/: push =
 * max(0, header bottom without our translate - top of the <footer> that holds .finv-section), written to the `translate`
 * property so it adds to the phone slide's `transform`. No footer-inova on the page = nothing; while the page is scroll-locked
 * (a drawer / dialog open: html[scroll-lock]) the push is left as it is.
 */
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const header = document.querySelector('header-component#header-component');

if (header) {
  const firstScrollTop = getScrollTop();
  const line = header.getBoundingClientRect().bottom + firstScrollTop + 100;
  let lastScrollTop = firstScrollTop;
  let frame = null;

  const footer = document.querySelector('.finv-section')?.closest('footer') || null;
  // what is sticky is the header's SECTION (header.shopify-section, z 18), not header-component inside it: moving the component
  // left the section's box over the footer and it still took the clicks (g106d/gate.log: 17/23 'header.shopify-section').
  // The section is not transformed by the phone slide (that moves the component), so its box is the natural one.
  const sticky = header.closest('.shopify-section') || header;
  // 248.0 HD-LAYER: the state is mirrored to the header section (always) and to <html> (only while the header is [sticky]), so the
  // rules that follow it (base.css, header.liquid) key on an attribute instead of :has() over the page - a flip no longer
  // re-checks every element (review-248/TO-HEAD.md Reports 4-7). Lines marked hd-mirror are the layer; the KA plant removes them.
  const mirror = () => { /* hd-mirror */
    const on = header.hasAttribute('data-hd-hidden'); /* hd-mirror */
    sticky.toggleAttribute('data-hd-hidden', on); /* hd-mirror */
    document.documentElement.toggleAttribute('data-hd-hidden', on && header.hasAttribute('sticky')); /* hd-mirror */
  }; /* hd-mirror */
  let push = 0;
  const pushOut = () => {
    if (!footer || document.documentElement.hasAttribute('scroll-lock')) return;
    // undo the translate that is APPLIED, not the one we stored: with the stored value, a blocked `translate` made the push grow
    // every frame (known-bad run: -516) and the header would jump that far once the blocker went away (head 60.0).
    const applied = getComputedStyle(sticky).translate;
    const shift = applied && applied !== 'none' ? parseFloat(applied.split(' ')[1] || '0') || 0 : 0;
    const bottom = sticky.getBoundingClientRect().bottom - shift;
    const next = Math.max(0, Math.round(bottom - footer.getBoundingClientRect().top));
    if (next === push) return;
    push = next;
    sticky.style.translate = push ? `0 ${-push}px` : '';
  };

  const update = () => {
    frame = null;
    const scrollTop = getScrollTop();
    header.toggleAttribute('data-hd-hidden', scrollTop > line && scrollTop > lastScrollTop);
    mirror(); /* hd-mirror */
    lastScrollTop = scrollTop;
    pushOut();
  };
  const onScroll = () => {
    if (frame === null) frame = requestAnimationFrame(update);
  };

  let target = getScrollEventTarget();
  target.addEventListener('scroll', onScroll, { passive: true });
  scrollContainerMediaQuery.addEventListener('change', () => {
    target.removeEventListener('scroll', onScroll);
    target = getScrollEventTarget();
    target.addEventListener('scroll', onScroll, { passive: true });
    header.removeAttribute('data-hd-hidden');
    mirror(); /* hd-mirror */
    lastScrollTop = getScrollTop();
    onScroll();
  });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();
}
